import {
  Node,
  Edge,
  MarkerType,
} from 'reactflow';
import i18n from '../../utils/i18n';
import { UniqueProcessType, NodeDataType, CycleTimeInSecondByPartNoType, EquipmentType, PartNo } from '../../contexts/types';
import ProcessNode from './ProcessNode';
import GroupNode from './GroupNode';

/**
 * カスタムノードタイプ
 */
export const nodeTypes = {
  custom: ProcessNode,
  group: GroupNode,
};

/** ノード幅 */
export const NODE_WIDTH = 150;
/** ノード高さ */
export const NODE_HEIGHT = 150;
/** ノード余白(上下) */
export const NODE_MARGIN_TB = 25;
/** ノード余白（左右） */
export const NODE_MARGIN_LR = 200;
/** グループ余白(上下) */
export const GROUP_MARGIN_TB = 10;
/** グループ余白（左右） */
export const GROUP_MARGIN_LR = 20;

/** 仮想プロセスID（複数ルート対応用） */
const PROCESS_ID_VIRTUAL = "#VIRTUAL#PROCESS#ID#";

/** パーツNo/サイクルタイム 文字色パレット */
export const colorPalette = ["blue.400", "green.400", "orange.400", "yellow.400", "purple.400", "pink.400", "teal.400", "cyan.400", "red.400", "gray.400"];

/**
 * プロセスフロー描画
 * @param uniqueProcessData ユニークプロセス情報
 * @param handleNodeClick ノード押下処理
 * @param equipmentData 設備情報
 * @param partNoList displayPartNo表示解決用のPartNo一覧
 * @returns {ユニークプロセス情報, ノード情報, エッジ情報}
 */
export const drawProcessFlow = (uniqueProcessData?: UniqueProcessType[], handleNodeClick?: (data: any) => {}, equipmentData?: EquipmentType[] | null, partNoList?: Pick<PartNo, "partNo" | "displayPartNo">[] | null) => {

  if (!uniqueProcessData || (uniqueProcessData?.length ?? 0) === 0) {
    return {};
  }

  // 階層構造構築
  const rootProcess = buildHierarchyProcess(uniqueProcessData);

  // 最大レベルの特定
  const maxLevelProcess = uniqueProcessData.reduce((max, current) => {
    return (current.uniqueLevel ?? Number.MAX_VALUE) > (max.uniqueLevel ?? Number.MAX_VALUE) ? current : max;
  });

  console.log(`Max Level = ${maxLevelProcess.uniqueLevel}`);

  // マトリクス位置の最適化
  buildOptimalPosition(rootProcess, 0, maxLevelProcess.uniqueLevel ?? 0);
  console.log(rootProcess);

  // プロセスフローデータ作成
  const { nodes, edges } = buildFlowData(rootProcess, handleNodeClick, equipmentData, partNoList);

  return {
    nodes: nodes,
    edges: edges,
  };

}

/**
 * プロセス情報を取得する。
 * @param projectId プロジェクトID
 * @param title トーストタイトル
 * @param referencingProjectId 参照元案件ID（過去PJ参照時のみ。現在表示中の案件ID）
 * @returns プロセス情報
 */
export const fetchProcess = async (projectId: string, title: string | null, WebAPI: any, toast: any | null, reviewDate?: string | null, referencingProjectId?: string | null) => {
  const payload = {
    query: `
          query getUniqueProcessByProjectId($projectId: String, $reviewDate: String, $referencingProjectId: String) {
            getUniqueProcessByProjectId(projectId: $projectId, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
              uniqueProcessId
              uniqueProcessName
              uniqueProcessSeq
              uniqueLevel
              uniqueProcessCycleTimeInSecondByPartNo
              updateDate
            }
          }`,
    variables: { projectId, ...(reviewDate && { reviewDate }), ...(referencingProjectId && { referencingProjectId }) }
  };

  // DB情報取得
  const response = await WebAPI.request(payload);

  if (response?.data?.getUniqueProcessByProjectId) {
    const uniqueProcessData: UniqueProcessType[] = response.data.getUniqueProcessByProjectId;

    convertCycleTimeInSecondByPartNoLis(uniqueProcessData);

    // パーツNoでソート
    uniqueProcessData.sort((a, b) => {
      if ((a.partNo ?? "") < (b.partNo ?? "")) return -1;
      if ((a.partNo ?? "") > (b.partNo ?? "")) return 1;
      return a.uniqueProcessSeq - b.uniqueProcessSeq;
    });

    return uniqueProcessData;
  } else {
    if (title && toast) {
      // データ取得失敗の場合
      toast({
        title: title,
        description: i18n.t('features.InvestmentScreening.ProcessFlow.message.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    console.error(`Failed to call API getUniqueProcess. errors = `, response?.errors);
  }
}

/**
 * displayPartNo表示解決用にPartNo一覧（partNo, displayPartNoのみ）を取得する。
 * @param projectId プロジェクトID
 * @param WebAPI WebAPIContext
 * @param reviewDate 振り返り日（振り返りモード以外はnull/undefined）
 * @returns PartNo一覧（取得失敗時は空配列）
 */
export const fetchPartNoListForDisplay = async (projectId: string, WebAPI: any, reviewDate?: string | null): Promise<Pick<PartNo, "partNo" | "displayPartNo">[]> => {
  const payload = {
    query: `
          query getPartNo($projectId: String, $reviewDate: String) {
            getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
              partNo
              displayPartNo
            }
          }`,
    variables: { projectId, ...(reviewDate && { reviewDate }) }
  };

  const response = await WebAPI.request(payload);

  if (response?.data?.getPartNo) {
    return response.data.getPartNo;
  }

  console.error(`Failed to call API getPartNo. errors = `, response?.errors);
  return [];
}

/**
 * サイクルタイム/パーツNoを変換する。
 * @param uniqueProcessData プロセス情報
 */
export const convertCycleTimeInSecondByPartNoLis = (uniqueProcessData: UniqueProcessType[]) => {
  uniqueProcessData.forEach((record) => {
    // サイクルタイム/PartNo（秒）
    const cycleTimesRaw = record.uniqueProcessCycleTimeInSecondByPartNo;

    let cycleTimes: CycleTimeInSecondByPartNoType[] = [];
    if (cycleTimesRaw) {
      try {
        cycleTimes = JSON.parse(cycleTimesRaw);
      } catch (e) {
        console.error("JSON parse error:", e);
      }
    }

    // サイクルタイム/PartNo（秒）配列展開後、ソートして格納
    record.uniqueProcessCycleTimeInSecondByPartNoList = cycleTimes.sort((a, b) => {
      if (a.partNo < b.partNo) return -1;
      else if (a.partNo > b.partNo) return 1;
      else return 0;
    });

    return record;
  });
}

/**
 * 階層構造構築
 * @param uniqueProcessData プロセス情報
 * @returns 階層構造構築のプロセス情報
 */
export const buildHierarchyProcess = (uniqueProcessData: UniqueProcessType[]): UniqueProcessType => {
  if (!uniqueProcessData || uniqueProcessData.length === 0) {
    return {} as UniqueProcessType;
  }

  // シーケンスでソート
  uniqueProcessData.sort((a, b) => a.uniqueProcessSeq - b.uniqueProcessSeq);

  // レベルの最小値を取得
  let rootProcess = uniqueProcessData.reduce((min, current) => {
    return (current.uniqueLevel ?? Number.MAX_VALUE) < (min.uniqueLevel ?? Number.MAX_VALUE) ? current : min;
  });

  const minLevel = rootProcess?.uniqueLevel || 0;
  const minProcesses = uniqueProcessData.filter(u => u.uniqueLevel === minLevel);
  if (minProcesses.length > 1) {
    // 最小レベルが複数の場合、仮の親プロセスを生成
    rootProcess = {
      uniqueProcessSeq: 0,
      uniqueLevel: minLevel - 1,
      uniqueProcessId: PROCESS_ID_VIRTUAL,
      uniqueProcessName: i18n.t('features.InvestmentScreening.ProcessFlow.virtualProcessName'),
      posX: 0,
      posY: 0,
    };
    uniqueProcessData.unshift(rootProcess);
  }


  const firstIndex = uniqueProcessData.indexOf(rootProcess) + 1;
  buildSubProcess(uniqueProcessData, firstIndex, rootProcess);

  return rootProcess;
}

/**
 * 階層構造構築
 * @param uniqueProcessData プロセス情報
 * @param firstIndex 開始位置
 * @param parent 親プロセス
 * @returns 再帰処理からの復帰位置
 */
export const buildSubProcess = (uniqueProcessData: UniqueProcessType[], firstIndex: number, parent: UniqueProcessType | null) => {
  let currentProcess: UniqueProcessType | null = null;
  const parentLevel = parent?.uniqueLevel ?? Number.MAX_VALUE;

  let i = firstIndex || 0;
  for (i = firstIndex; i < uniqueProcessData.length; i++) {

    const subProcess: UniqueProcessType = uniqueProcessData[i];
    const subLevel = subProcess.uniqueLevel ?? Number.MAX_VALUE;

    if (subLevel === (parentLevel + 1)) {
      // 子プロセス（１つ下のレベル）
      if (parent) {
        if (!parent.subProcess) {
          parent.subProcess = [];
        }
        parent.subProcess.push(subProcess);
      }
      currentProcess = subProcess;

    } else if (subLevel === (parentLevel + 2)) {
      if (!currentProcess) {
        // 無限ループ防止
        continue;
      }
      // 子プロセス ⇒ 孫プロセス（２つ下のレベル）
      i = buildSubProcess(uniqueProcessData, i, currentProcess) ?? 0;
    } else {
      // １つ上のレベルへ戻る
      return i - 1;
    }
  }

  return i;
}

/**
 * プロセスのマトリクス位置を最適化構築する。
 * @param process プロセス情報
 * @param firstPosY Y軸の初期位置
 * @param maxLevel 最大レベル（X軸判定で参照）
 * @param reservePos 予約済み位置連想配列（キー：[posX]-[posY] 値：true 使用済み、false 未使用 ）
 */
export const buildOptimalPosition = (process: UniqueProcessType, firstPosY: number, maxLevel: number, reservePos?: Record<string, boolean>) => {

  if (!reservePos) {
    reservePos = {};
  }

  let posY: number = firstPosY;

  if (process.uniqueProcessId !== PROCESS_ID_VIRTUAL) {
    process.posX = maxLevel - (process.uniqueLevel ?? 0);
    process.posY = posY;

    if (reservePos[`${process.posX}-${process.posY}`]) {
      posY++;
      process.posY = posY;
    } else {
      reservePos[`${process.posX}-${process.posY}`] = true;
    }
  } else {
    maxLevel = maxLevel - 1;
  }

  // デバッグ用
  // console.warn(`★ ${process.uniqueProcessId} - posX = ${process.posX}, posY == ${process.posY}`);

  // 子プロセスあり
  let prevSubProcess: UniqueProcessType | null = null;
  for (const subProcess of process.subProcess ?? []) {
    if (prevSubProcess && (prevSubProcess.subProcess?.length ?? 0) > 0 && ((subProcess.subProcess?.length ?? 0) > 0)) {
      // 1つ前のプロセスに子がいる場合は、それを加味する。
      posY = prevSubProcess.posY + (prevSubProcess.subProcess?.length ?? 0);
    }

    buildOptimalPosition(subProcess, posY, maxLevel, reservePos);
    posY++;
    prevSubProcess = subProcess;
  }
}

/**
 * プロセスフローデータ作成
 * @param rootProcess ルートプロセス情報
 * @param handleNodeClick ノード押下処理
 * @param equipmentData 設備情報
 * @returns {ノード情報・エッジ情報}
 */
export const buildFlowData = (rootProcess: UniqueProcessType, handleNodeClick?: (data: any) => {}, equipmentData?: EquipmentType[] | null, partNoList?: Pick<PartNo, "partNo" | "displayPartNo">[] | null) => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const createNodeId = (process: UniqueProcessType) => `node-${process.posX}-${process.posY}`;
  const createGroupId = (process: UniqueProcessType) => `group-${process.posX}-${process.posY}`;
  const createEdgeId = (source: string, target: string) => `edgeId-${source}-${target}`;

  // パーツNo/サイクルタイム 文字色
  const colors: { [partNo: string]: { color: string } } = {};

  // カラーインデックス
  let colorIndex = 0;

  // ノード作成
  const createNode = (process: UniqueProcessType, left: boolean, right: boolean): Node => {
    const posX = process.posX;
    const posY = process.posY;

    const data: NodeDataType = {
      process: process,
      equipmentData: equipmentData?.filter(e => e.processId === process.uniqueProcessId),
      onClick: handleNodeClick,
      left: left,
      right: right,
      colors: colors,
      partNoList: partNoList,
    };

    // ノード情報
    const node: Node = {
      id: createNodeId(process),
      type: 'custom',
      position: { x: (NODE_WIDTH * posX) + (NODE_MARGIN_LR * posX), y: (NODE_HEIGHT * posY) + (NODE_MARGIN_TB * posY) },
      style: { width: NODE_WIDTH, height: NODE_HEIGHT },
      data: data,
    };

    return node;
  }

  // グループノード作成
  const createGroup = (process: UniqueProcessType, groupCount: number): Node => {
    const posX = process.posX;
    const posY = process.posY;
    const groupNode: Node = {
      id: createGroupId(process),
      type: 'group',
      position: { x: (NODE_WIDTH * posX) + (NODE_MARGIN_LR * posX) - GROUP_MARGIN_LR, y: (NODE_HEIGHT * posY) + (NODE_MARGIN_TB * posY) - GROUP_MARGIN_TB },
      style: { width: NODE_WIDTH + (GROUP_MARGIN_LR * 2), height: (NODE_HEIGHT * groupCount) + (GROUP_MARGIN_TB * 2 * (groupCount)) + ((NODE_MARGIN_TB - (GROUP_MARGIN_TB * 2)) * (groupCount - 1)) },
      data: {
        left: false,
        right: true
      },
    };

    return groupNode;
  }

  // エッジ作成
  const createEdge = (source: string, target: string) => {
    if (!source || !target) {
      return null;
    }

    const edge: Edge = {
      id: createEdgeId(source, target),
      source: source,
      target: target,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 30,
        height: 30,
        color: '#000',
      },
      style: {
        strokeWidth: 1,
        stroke: '#000',
      },
    };

    return edge;
  }

  // 再帰的にフローデータを作成する。
  const subBuildFlowData = (process: UniqueProcessType | null | undefined, groupCount: number | null, parentNode: Node | null) => {
    if (!process) {
      return;
    }

    if (groupCount && groupCount > 1) {
      // ■ グループノード作成
      const groupNode = createGroup(process, groupCount);
      nodes.push(groupNode);

      const edge = createEdge(groupNode.id, parentNode?.id ?? "");
      if (edge) {
        edges.push(edge);
      }
    }

    // ■ ノード作成
    const left = (process.subProcess?.length ?? 0) > 0;
    const right = groupCount ? (groupCount === 1) : false;

    // パーツNoごとに同じ色を設定
    process.uniqueProcessCycleTimeInSecondByPartNoList?.map((p, idx) => {
      if (!colors.hasOwnProperty(p.partNo)) {
        // 存在しない場合、色を設定
        colors[p.partNo] = {
          color: colorPalette[colorIndex]
        };
        colorIndex++;
        if (colorPalette.length <= colorIndex) {
          // パレットが一巡したら、最初から色を振る
          colorIndex = 0;
        }
      }
    });

    const node: Node = createNode(process, left, right);
    nodes.push(node);

    if (groupCount && groupCount === 1) {
      const edge = createEdge(node.id, parentNode?.id ?? "");
      if (edge) {
        edges.push(edge);
      }
    }

    for (let i = 0; i < (process.subProcess ?? []).length; i++) {
      const subProcess = process.subProcess?.[i];

      // グループノードは先頭ノードの作成時のみ作成するようにする。
      let groupEnd: number | null = null;
      if (i === 0) {
        const subProcessChildren = (process.subProcess ?? []);
        if (subProcessChildren.length > 0) {
          const lastSubProcessChild = subProcessChildren[subProcessChildren.length - 1];
          // 最後の縦位置 - 最初の縦位置 + 1がグループの高さ
          groupEnd = lastSubProcessChild.posY - (subProcess?.posY ?? 0) + 1;
        }
      }

      // 再帰処理
      subBuildFlowData(subProcess, groupEnd, node);
    }
  }

  if (rootProcess.uniqueProcessId === PROCESS_ID_VIRTUAL) {
    for (let i = 0; i < (rootProcess.subProcess ?? []).length; i++) {
      const subProcess = rootProcess.subProcess?.[i];
      subBuildFlowData(subProcess, null, null);
    }
  } else {
    subBuildFlowData(rootProcess, null, null);
  }

  // ノード・エッジ返却
  return { nodes: nodes, edges: edges };
}
